import React, { useState } from 'react';
import { Box, Button, CircularProgress, Divider, Grid, IconButton, Modal, Paper, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import useSubscriptionCheck from '../../common/SubscriptionCheck';
import BulkUploadTemplateSelector from './BulkUploadTemplateSelector';
import FileUpload from '../../../components/FileUpload';
import { apiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useCreateBulkUploadHandler } from '../dataHandlers/createBulkUploadHandler';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import { WRITE_BULK_UPLOAD } from '../../../constants/eInvoicePermissions';

const CUSTOMER_MAX_FILE_SIZE = 5 * 1024 * 1024;
const SUPPLIER_MAX_FILE_SIZE = 5 * 1024 * 1024;
const PRODUCT_MAX_FILE_SIZE = 5 * 1024 * 1024;
const DEFAULT_BULK_UPLOAD_MAX_FILE_SIZE = 10 * 1024 * 1024;

const BulkUploadButton = ({ category = 'E_INVOICE', subCategory = null, redirectAfterSubmit, requiredPermission }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const { checkSubscription, SubscriptionModal } = useSubscriptionCheck();
  const subscription = useSelector((state) => state.subscription.subscription);
  const dispatch = useDispatch();
  const { eInvoiceForm } = useTranslatedMessages();
  const { bulk_upload } = eInvoiceForm;
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const isBulkUploadFeatureEnabled = subscription?.features?.enable_bulk_upload;
  const isSftpFeatureEnabled = subscription?.features?.enable_sftp;

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { onSubmit, isPending } = useCreateBulkUploadHandler(() => {
    setOpen(false);
  }, redirectAfterSubmit);

  const isPermissionDenied =
    !hasEInvoicePermission(WRITE_BULK_UPLOAD) ||
    Boolean(requiredPermission && !hasEInvoicePermission(requiredPermission));

  const handleOpen = () => {
    if (isPermissionDenied || !checkSubscription()) {
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const maxFileSize =
    category === 'CUSTOMER'
      ? CUSTOMER_MAX_FILE_SIZE
      : category === 'SUPPLIER'
        ? SUPPLIER_MAX_FILE_SIZE
        : category === 'PRODUCT'
          ? PRODUCT_MAX_FILE_SIZE
          : DEFAULT_BULK_UPLOAD_MAX_FILE_SIZE;

  const getModalTitle = () => {
    if (category === 'CUSTOMER') {
      return bulk_upload.title_customer ?? 'Bulk Upload Customers';
    }
    if (category === 'SUPPLIER') {
      return bulk_upload.title_supplier ?? 'Bulk Upload Suppliers';
    }
    if (category === 'PRODUCT') {
      return bulk_upload.title_product ?? 'Bulk Upload Products';
    }
    if (category === 'NON_E_INVOICE') {
      return bulk_upload.title_non_e_invoice;
    }
    return bulk_upload.title_e_invoice;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) {
      alert(bulk_upload.alert_select_file);
      return;
    }
    if (!selectedTemplate) {
      alert(bulk_upload.alert_select_template);
      return;
    }
    onSubmit(file, category, subCategory, selectedTemplate);
  };

  const onTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const onFileUpload = (file) => {
    setFile(file);
  };

  const onDownloadTemplate = () => {
    setDownloadingTemplate(true);
    apiClient
      .get('/bulk-uploads/template/download', {
        responseType: 'blob',
        params: { template_name: selectedTemplate },
      })
      .then((response) => {
        dispatch(showSnackbar({ message: bulk_upload.template_download_success, severity: 'success' }));
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${selectedTemplate}.xlsx`);
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          showActionDeniedSnackbar();
          return;
        }
        dispatch(
          showSnackbar({
            message: bulk_upload.template_download_error,
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        setDownloadingTemplate(false);
      });
  };

  if (!isBulkUploadFeatureEnabled && !isSftpFeatureEnabled) {
    return null;
  }

  return (
    <>
      <PermissionDeniedWrapper show={isPermissionDenied} title={accessDeniedMessage}>
        <Button id="bulk-upload" variant="contained" color="primary" onClick={handleOpen} disabled={isPermissionDenied}>
          {bulk_upload.button_label}
        </Button>
      </PermissionDeniedWrapper>
      <Modal open={open} onClose={handleClose}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            p: 2,
            boxShadow: 24,
            outline: 'none',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              {getModalTitle()}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          <Grid sx={{ pt: 1 }}>
            <Typography variant="subtitle2" sx={{ py: 1 }}>
              {bulk_upload.step_select_template}
            </Typography>
            <BulkUploadTemplateSelector onSelect={onTemplateSelect} category={category} subCategory={subCategory} />
          </Grid>
          <Grid sx={{ pt: 1 }}>
            <Typography variant="subtitle2" sx={{ py: 1 }}>
              {bulk_upload.step_upload_file}
            </Typography>
            <FileUpload onUpload={onFileUpload} maxSize={maxFileSize} supportedFormats={['.xlsx']} />
            <Grid alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                {bulk_upload.helper_download_here}
              </Typography>
              <Button
                variant="text"
                color="primary"
                disabled={!selectedTemplate || downloadingTemplate}
                onClick={onDownloadTemplate}
                startIcon={downloadingTemplate ? <CircularProgress size={20} /> : null}
              >
                {downloadingTemplate ? bulk_upload.downloading_template : bulk_upload.download_template}
              </Button>
            </Grid>
          </Grid>
          <Divider />
          <Grid mt={2} textAlign="right">
            <Button
              id="upload-start-processing"
              variant="contained"
              color="primary"
              disabled={!file || isPending || downloadingTemplate}
              startIcon={isPending ? <CircularProgress size={20} /> : null}
              onClick={handleSubmit}
            >
              {isPending ? bulk_upload.processing : bulk_upload.start_processing}
            </Button>
          </Grid>
        </Paper>
      </Modal>
      <SubscriptionModal />
    </>
  );
};

export default BulkUploadButton;
